import { v4 as uuidV4 } from 'uuid';
import { useBoolean } from 'react-use';
import { convertToIanaTimezone } from '@/utils/helpers/index';
import { useUploadFile } from '@/hooks/useUploadFile';
import { FileType } from '@/queries/file/index';
import { useCreateHospitalMutation } from '@/queries/hospital/index';
import { retrieveUnifiedError } from '@/api/utils';
import { Hospital } from '@/api/apis/facilities/index';
import { NewHospitalForm } from '@/types/facility.types';

export const useCreateHospital = () => {
  const { mutateAsync } = useCreateHospitalMutation();
  const { uploadFiles } = useUploadFile(FileType.Facility);
  const [isSubmitting, setIsSubmitting] = useBoolean(false);

  const prepareHospitalData = async (data: NewHospitalForm) => {
    let uploadedHospitalFile;
    let uploadedContactFile;

    if (data.image) {
      uploadedHospitalFile = await uploadFiles([data.image]);
    }

    if (data.photo) {
      uploadedContactFile = await uploadFiles([data.photo]);
    }

    return {
      id: uuidV4(),
      name: data.name,
      code: data.code,
      timezone: data.timezoneIANA || convertToIanaTimezone(data.timezone),
      addressLine: data.addressLine,
      city: data.city,
      state: data.state,
      postalCode: data.postalCode,
      country: data.country,
      longitude: data.longitude,
      latitude: data.latitude,
      logoPath: uploadedHospitalFile ? uploadedHospitalFile[0].fileName : undefined,
      primaryContact: {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        primaryPhone: data.primaryPhone,
        secondaryPhone: data.secondaryPhone,
        photoPath: uploadedContactFile ? uploadedContactFile[0].fileName : undefined,
      },
    };
  };

  const createHospital = async (data: NewHospitalForm) => {
    try {
      setIsSubmitting(true);
      const hospitalData = await prepareHospitalData(data);
      const hospital: Hospital = await mutateAsync(hospitalData);
      return hospital;
    } catch (e) {
      const error = retrieveUnifiedError(e);
      throw new Error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, createHospital };
};
